'use client'

import React, { useState, useRef, useEffect } from 'react'
import { PlayVideoIcon } from '../icons'
import { ImageWithCustomLoader } from '../image-with-custom-loader'

const DEFAULT_PLACEHOLDER_RESOLUTION = 600

type VideoDisplayProps = {
  className?: string
  src: string
  videoStyle?: string
  placeHolderStyle?: React.CSSProperties
  placeholderSrc: string
  blurHash?: string
  onClick?: React.MouseEventHandler<HTMLElement>
  autoPlay?: boolean
  paused?: boolean
  placeHolderImageHeight?: number
  placeHolderImageWidth?: number
  muted?: boolean
  controls?: boolean
  onLoad?: () => void
}

export const VideoDisplay = ({
  className,
  placeholderSrc,
  videoStyle,
  placeHolderStyle,
  blurHash,
  onClick,
  autoPlay,
  placeHolderImageHeight,
  placeHolderImageWidth,
  muted,
  paused,
  controls = true,
  onLoad,
  ...props
}: VideoDisplayProps): React.ReactElement | null => {
  // due to inconsistencies with onLoadedMetadata firing, check for onLoadedMetadata, onCanPlay, and onCanPlayThrough to indicate video has loaded
  const [loading, setIsLoading] = useState(autoPlay ? false : true)
  const [showVideo, setShowVideo] = useState(false)
  const [showIcon, setShowIcon] = useState(false)
  const [hover, setHover] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  const getIconFill = () => {
    if (!hover) {
      return '.30'
    } else {
      return '1.0'
    }
  }

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play()
    }
    setShowVideo(true)
  }

  useEffect(() => {
    if (autoPlay) {
      handlePlay()
    }
  }, [autoPlay])

  useEffect(() => {
    if (paused) {
      if (videoRef.current) {
        videoRef.current.pause()
      }
    } else {
      handlePlay()
    }
  }, [paused])

  if (!props.src) {
    return null
  }

  return (
    <div className={`relative flex h-full w-full items-end justify-end ${className}`}>
      <video
        ref={videoRef}
        className={`h-full w-full ${!loading && showVideo ? 'block' : 'hidden'} object-contain ${videoStyle}`}
        onLoadStart={autoPlay ? undefined : () => setIsLoading(true)}
        onLoadedMetadata={() => setIsLoading(false)}
        onCanPlay={() => {
          setIsLoading(false)
          onLoad?.()
        }}
        onCanPlayThrough={() => setIsLoading(false)}
        onEnded={autoPlay ? undefined : () => setShowVideo(false)}
        controls={controls}
        preload="metadata"
        onClick={onClick}
        loop={autoPlay}
        autoPlay={autoPlay}
        muted={muted}
      >
        <source src={props.src} />
      </video>
      <PlayVideoIcon
        className={`absolute z-10 m-auto cursor-pointer ${(!loading && showVideo) || !showIcon ? 'hidden' : 'block'}`}
        fillOpacity={getIconFill()}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => handlePlay()}
      />
      <ImageWithCustomLoader
        className={`block h-full object-cover duration-300 ${!loading && showVideo ? 'hidden' : 'block'}`}
        alt="video placeholder image"
        height={placeHolderImageHeight ?? DEFAULT_PLACEHOLDER_RESOLUTION}
        width={placeHolderImageWidth ?? DEFAULT_PLACEHOLDER_RESOLUTION}
        src={placeholderSrc}
        onLoad={() => {
          setShowIcon(true)
        }}
        onClick={onClick}
        style={placeHolderStyle}
        blurHash={blurHash}
      />
    </div>
  )
}
